import React from 'react'
import styles from './InvoiceDetails.module.scss'
import Layout from '../../components/core/Layout';
import { useParams } from 'react-router-dom';
import { getTaxRate, getLogisticFee, postLogisticFee } from '../../apis/invoice-api';
import { INVOICE_TAB_INDEX, InvStatus } from '../../libs/constants';
import { str2number } from '../../libs/helpers';
import { InvoiceMasterData } from "../../apis/models/InvoiceMasterRequest";
import { error, success } from '../../apis/reducers/snackbarsReducer';
import { useDispatch, useSelector } from 'react-redux';
import { getInvoiceMaster, putInvoiceMaster } from '../../apis/invmaster-api';
import { TaxResponseData } from '../../apis/models/TaxResponse';
import DateWithoutTime from '../../libs/dateWithoutTime';
import InvoiceHeader from '../../components/InvoiceDetailsPage/header/InvoiceHeader';
import { RootState } from '../../apis/store';
import { saveMasterInfo } from '../../apis/reducers/invoiceMasterReducer';
import LogisticsFee, { LogisticFeeTabType } from '../../components/InvoiceDetailsPage/LogisticsFee';
import UploadLogisticsCsvDialog from '../../components/dialogs/invoiceDetails/UploadLogisticsCsvDialog';
import { LogisticsFeeResponseData } from '../../apis/models/LogisticsFeeResponse';
import { LogisticsFeeRequestItem } from '../../apis/models/LogisticsFeeRequest';
import ResetDialog from '../../components/dialogs/confirmDialogs/ResetDialog';

const InvoiceLogisticFeePage = () => {
  const invoiceMasterState = useSelector((state: RootState) => state.invoiceMaster);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [invoiceMasterData, setInvoiceMasterData] = React.useState<InvoiceMasterData | null>(invoiceMasterState.masterData);
  const [taxItem, setTaxItem] = React.useState<TaxResponseData | null>(null);
  const [logisticFees, setLogisticFees] = React.useState<LogisticFeeTabType[]>([]);
  const [notYetSaved, setNotYetSaved] = React.useState<boolean>(false);
  const [openUploadDialog, setOpenUploadDialog] = React.useState<boolean>(false);
  const [openWarningDialog, setOpenWarningDialog] = React.useState<boolean>(false);
  const [isChanged, setIsChanged] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (id) {
      loadAllData();
    }
  },[id])

  const hasError = React.useMemo(() => {
    return logisticFees.some(fee => {
      return (
        fee.error.dateAdded 
        || fee.error.inspectionQuantity 
        || fee.error.inspectionUnitPrice
        || fee.error.shippingQuantity
        || fee.error.shippingUnitPrice
      )
    })
  },[logisticFees]);

  const loadAllData = () => {
    getInvoiceMasterData();
    getLogisticFeeDetails();
    setNotYetSaved(false);
  }

  const onSaveAndCommit = async () => {
    const isSuccess = await saveLogisticFeeDetails();
    if (isSuccess){
      onChangeInvoiceStatus(InvStatus.CONFIRMED)
    }
  }

  const onChangeInvoiceStatus = async (status: InvStatus) => {
    try {
      await putInvoiceMaster(id!, {status: status})
      dispatch(success("請求ステータスを変更しました"));
      loadAllData();
    }catch (err) {
      dispatch(error("請求ステータスの変更に失敗しました"));
    }
  }

  const getInvoiceMasterData = () => {
    getInvoiceMaster(id)
    .then(function (response) {
        setInvoiceMasterData(response.data.data);
        dispatch(saveMasterInfo({masterData: response.data.data}))
        getTaxRate(new Date(response.data.data.from).toString())
          .then(res => setTaxItem(res.data.data))
          .catch(err => console.log(err))
    })
    .catch(function (error) {
        console.log(error);
    })
  }

  const getLogisticFeeDetails = () => {
    if (id !== undefined) {
      getLogisticFee(id)
        .then((res) => {
          const logisticsFeesResponse = convertResponseToTabType(res.data.data);
          setLogisticFees(sortLogisticsFees(logisticsFeesResponse));
        })
        .catch((err) => {})
    }
  }

  const sortLogisticsFees = (fees: LogisticFeeTabType[]) => [...fees].sort((a, b) => DateWithoutTime.sortAsc(a.dateAdded, b.dateAdded));

  const convertResponseToTabType = (datas: LogisticsFeeResponseData[]): LogisticFeeTabType[] => {
    return datas.map((fee: LogisticsFeeResponseData) => {
      return {
        dateAdded: new DateWithoutTime(fee.dateAdded),
        productNumber: fee.productNumber,
        colors: fee.colors,
        inspectionUnitPrice: String(fee.inspectionUnitPrice),
        inspectionQuantity: String(fee.inspectionQuantity),
        shippingUnitPrice: String(fee.shippingUnitPrice),
        shippingQuantity: String(fee.shippingQuantity),
        memo: String(fee.memo),
        error: {}
      } as LogisticFeeTabType;
    })
  }

  const onChangeFees = (items: LogisticFeeTabType[]) => {
    setLogisticFees(sortLogisticsFees(items));
    setNotYetSaved(true);
  }

  const saveLogisticFeeDetails = async (): Promise<boolean> => {
    if (!id) {
      return false
    }
    const req: LogisticsFeeRequestItem[] = [];
    logisticFees.forEach(item => {
      if (!item.dateAdded.value) {
        return;
      }
      const data: LogisticsFeeRequestItem = {
        invoiceMasterId: id!,
        dateAdded: item.dateAdded.getValue4Server(),
        productNumber: item.productNumber,
        colors: item.colors,
        inspectionQuantity: str2number(item.inspectionQuantity),
        inspectionUnitPrice: str2number(item.inspectionUnitPrice),
        shippingQuantity: str2number(item.shippingQuantity),
        shippingUnitPrice: str2number(item.shippingUnitPrice),
        memo: item.memo
      }
      req.push(data);
    })
    try {
      await postLogisticFee(id, req)
      dispatch(success("保存しました"));
      loadAllData();

      return true;
    } catch (e) {
      dispatch(error("保存に失敗しました"))
    }
    return false;
  }

  const onClickUpload = () => {
    if (!notYetSaved) {
      setOpenUploadDialog(true);
    } else {
      setOpenWarningDialog(true);
    }
  }

  return (
    <Layout withBreadcrumbs={true} bgColor="#FAFAFA">
      <React.Fragment>
        <div className={styles.Gray}>
          <div>
            <InvoiceHeader
              invoiceMaster={invoiceMasterData ?? undefined}
              tax={taxItem ?? undefined}
              selectedTab={INVOICE_TAB_INDEX.LOGISTIC}
              needSave={notYetSaved}
              error={hasError}
              onSaveAndCommit={() => onSaveAndCommit()}
              onChangeStatus={(status) => onChangeInvoiceStatus(status)}
              onMasterChange={() => loadAllData()}
            />
          </div>
          <div  className={styles.tabs}>
            <div className={styles.tabs__body}>
              {invoiceMasterData &&
                <LogisticsFee
                  targetInvoice={invoiceMasterData}
                  tabValues={logisticFees}
                  isChanged={isChanged}
                  onNotYetConfirmed={() => onChangeInvoiceStatus(InvStatus.NOT_CONFIRMED)}
                  onReset={() => loadAllData()}
                  onSave={() => saveLogisticFeeDetails()}
                  onChange={(items) => {onChangeFees(items); setIsChanged(true)}}
                  onClickUpload={() => onClickUpload()}
                />
              }
            </div>
          </div>
        </div>
        { openWarningDialog &&
          <ResetDialog
            open={openWarningDialog}
            onClose={() => setOpenWarningDialog(false)}  
            onOk={() => {setOpenWarningDialog(false); setOpenUploadDialog(true)}}
          />
        }
        { openUploadDialog &&
          <UploadLogisticsCsvDialog
            open={openUploadDialog}
            onClose={() => setOpenUploadDialog(false)}
            onSuccess={() => loadAllData()}
          />
        }
      </React.Fragment>
    </Layout>
  )
}

export default InvoiceLogisticFeePage